import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { methodologyService, Methodology, neuralNetworkService, NeuralPathway, WriteupEntry } from "../../lib/firebase";
import { useSystemSettings } from "../../lib/SystemSettingsContext";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/alert";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { MethodologyVisualization } from "../../components/ui/methodology-visualization";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { toast } from "../../components/ui/use-toast";

// Utility function to truncate AI reasoning
const truncateReasoning = (reasoning: string, maxLength: number = 300) => {
  if (reasoning.length <= maxLength) return reasoning;
  return reasoning.substring(0, maxLength) + "...";
};

export const PathwayMethodology = () => {
  const [userStage, setUserStage] = useState("");
  const [userTarget, setUserTarget] = useState("");
  const [userFindings, setUserFindings] = useState("");
  const [extraContext, setExtraContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [neuralPathway, setNeuralPathway] = useState<NeuralPathway | null>(null);
  const [recentPathways, setRecentPathways] = useState<NeuralPathway[]>([]);
  const [methodology, setMethodology] = useState<Methodology | null>(null);
  const [viewMode, setViewMode] = useState<'step' | 'flowchart'>('step');
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [expandedReasoning, setExpandedReasoning] = useState<Set<number>>(new Set());
  const [feedback, setFeedback] = useState({
    rating: 5,
    feedback: '',
    wasHelpful: true,
    improvements: []
  });
  const [sessionData, setSessionData] = useState<any>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useSystemSettings();
  const auth = getAuth();

  // Check if this is a new session from the URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const isNewSession = searchParams.get('newSession') === 'true';

  useEffect(() => {
    setLoading(true);
    
    // If this is a new session, call the new API endpoint
    if (isNewSession) {
      const initializeNewSession = async () => {
        try {
          const response = await fetch('/api/methodology/pathway?newSession=true', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            throw new Error('Failed to initialize new session');
          }

          const data = await response.json();
          if (data.success) {
            setSessionData(data.session);
            toast({
              title: "New Session Created",
              description: "Your pathway session has been initialized successfully.",
            });
          }
        } catch (err: any) {
          console.error('Failed to initialize new session:', err);
          setError(err.message || "Failed to initialize new session");
        } finally {
          setLoading(false);
        }
      };

      initializeNewSession();
    } else {
      // Load methodology data as before
    const unsubscribe = methodologyService.subscribeToMethodology("pathway", (methodologyData) => {
      if (methodologyData) {
        setMethodology(methodologyData);
        setError(null);
      } else {
        setError("Failed to load Pathway Methodology data");
      }
      setLoading(false);
    });
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
    }
  }, [isNewSession]);

  useEffect(() => {
    // Load recent neural pathways
    const loadRecentPathways = async () => {
      try {
        const pathways = await neuralNetworkService.getNeuralPathways();
        setRecentPathways(pathways.slice(0, 5)); // Show last 5
      } catch (error) {
        console.error("Error loading recent pathways:", error);
      }
    };
    loadRecentPathways();
  }, []);

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setNeuralPathway(null);
    
    try {
      const pathway = await neuralNetworkService.generateNeuralPathway({
        userStage,
        userTarget,
        userFindings,
        extraContext
      });
      
      setNeuralPathway(pathway);
      setCurrentStepIdx(0);
      setShowFeedback(false);
      
      // Update recent pathways
      setRecentPathways(prev => [pathway, ...prev.slice(0, 4)]);
      
      toast({
        title: "Neural Pathway Generated",
        description: `Generated pathway with ${pathway.generatedPathway.steps.flatMap(s => s.referencedWriteups).length} writeups`,
      });
    } catch (err: any) {
      setError(err.message || "Failed to generate neural pathway");
      toast({
        title: "Generation Failed",
        description: err.message || "Failed to generate neural pathway",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFeedbackSubmit = async () => {
    if (!neuralPathway) return;
    
    try {
      await neuralNetworkService.submitPathwayFeedback(neuralPathway.id, feedback);
      
      toast({
        title: "Feedback Submitted",
        description: "Thank you! Your feedback helps improve the neural network.",
      });
      
      setShowFeedback(false);
      setFeedback({ rating: 5, feedback: '', wasHelpful: true, improvements: [] });
    } catch (error) {
      toast({
        title: "Feedback Error",
        description: "Failed to submit feedback. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return "bg-green-500";
    if (confidence >= 0.6) return "bg-yellow-500";
    return "bg-red-500";
  };

  if (settings.disablePathway) {
    return (
      <div className="container mx-auto px-4 pt-[160px]">
        <Alert className="mb-4 bg-yellow-50 border-yellow-200">
          <AlertTitle className="text-yellow-800">Neural Pathway Unavailable</AlertTitle>
          <AlertDescription className="text-yellow-700">
            The Neural Pathway methodology is currently disabled by the administrator.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="font-['Space_Grotesk'] text-black hidden lg:flex"
              >
                Back to Dashboard
              </Button>
              <div 
                onClick={handleSignOut}
                className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
              >
                <img className="w-6 h-6" alt="User" src="/user-icon.svg" />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 pt-[160px] pb-12">
        {error && (
          <Alert className="mb-4 bg-yellow-50 border-yellow-200">
            <AlertTitle className="text-yellow-800">Error</AlertTitle>
            <AlertDescription className="text-yellow-700">{error}</AlertDescription>
          </Alert>
        )}
        
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          <div className="mb-8 flex items-center gap-2">
            <button 
              onClick={() => navigate('/dashboard')}
              className="text-black opacity-60 hover:opacity-100 transition-opacity"
            >
              ← Back to Dashboard
            </button>
          </div>
          
          <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
            {methodology?.title || "Neural Pathway Methodology"}
          </h1>
          <p className="font-['Space_Grotesk'] text-sm text-black opacity-80 mb-6">
            {methodology?.description || "AI-powered penetration testing pathways that learn from cybersecurity writeups."}
          </p>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#daed6e] mx-auto mb-4"></div>
              <p className="text-lg font-medium">Generating Neural Pathway...</p>
              <p className="text-sm text-gray-600 mt-2">Analyzing writeups and learning patterns</p>
            </div>
          ) : (
            <div className="flex flex-col gap-6 items-center">
              {neuralPathway ? (
                <div className="w-full max-w-4xl mx-auto">
                  {/* Pathway Header */}
                  <Card className="mb-6">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-xl">Neural Pathway Generated</CardTitle>
                          <p className="text-sm text-gray-600 mt-1">
                            Based on {neuralPathway.generatedPathway.steps.flatMap(s => s.referencedWriteups).length} writeups
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-medium">Confidence:</span>
                            <Badge className={getConfidenceColor(neuralPathway.generatedPathway.overallConfidence)}>
                              {(neuralPathway.generatedPathway.overallConfidence * 100).toFixed(0)}%
                            </Badge>
                          </div>
                          <Progress value={neuralPathway.generatedPathway.overallConfidence * 100} className="w-24" />
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <h4 className="font-medium">Learning Insights:</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {neuralPathway.generatedPathway.learningInsights.map((insight, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-[#daed6e]">•</span>
                              {insight}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Pathway Steps */}
                      <Tabs value={viewMode} onValueChange={v => setViewMode(v as 'step' | 'flowchart')}>
                    <TabsList className="mb-6">
                          <TabsTrigger value="step">Step-by-Step</TabsTrigger>
                          <TabsTrigger value="flowchart">Flowchart</TabsTrigger>
                        </TabsList>
                    
                        <TabsContent value="step">
                      <div className="space-y-4">
                        {neuralPathway.generatedPathway.steps.map((step, index) => (
                          <motion.div
                            key={step.step}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="bg-[#f8fbe7] rounded-xl p-6 border border-[#daed6e]"
                          >
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[#daed6e] flex items-center justify-center text-lg font-bold text-black">
                                  {step.step}
                                </div>
                                <div>
                                  <h3 className="font-bold text-lg text-black">{step.title}</h3>
                                  <div className="flex items-center gap-2 mt-1">
                                    <Badge className={getConfidenceColor(step.confidence)}>
                                      {(step.confidence * 100).toFixed(0)}% confidence
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <p className="text-gray-700 mb-4">{step.description}</p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              <div>
                                <h4 className="font-medium text-black mb-2">Tools:</h4>
                                <div className="flex flex-wrap gap-2">
                                  {step.tools.map((tool, idx) => (
                                    <Badge key={idx} variant="outline" className="bg-white">
                                      {tool}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <h4 className="font-medium text-black mb-2">Commands:</h4>
                                <div className="space-y-1">
                                  {step.commands.map((cmd, idx) => (
                                    <code key={idx} className="block text-sm bg-gray-100 p-2 rounded">
                                      {cmd}
                                    </code>
                                  ))}
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-white rounded-lg p-4">
                              <h4 className="font-medium text-black mb-2">AI Reasoning:</h4>
                              <p className="text-sm text-gray-600">
                                {expandedReasoning.has(index) ? step.reasoning : truncateReasoning(step.reasoning)}
                              </p>
                              {step.reasoning.length > 300 && (
                                <div className="text-center mt-2">
                                  <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => setExpandedReasoning(prev => {
                                      const newSet = new Set(prev);
                                      newSet.has(index) ? newSet.delete(index) : newSet.add(index);
                                      return newSet;
                                    })}
                                    className="text-[#daed6e] hover:text-[#c5d85c]"
                                  >
                                    {expandedReasoning.has(index) ? "Show less" : "Show more"}
                                  </Button>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                      
                      {/* Feedback Section */}
                      {!showFeedback ? (
                        <div className="mt-6 text-center">
                          <Button 
                            onClick={() => setShowFeedback(true)}
                            className="bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                          >
                            Provide Feedback
                          </Button>
                        </div>
                      ) : (
                        <Card className="mt-6">
                          <CardHeader>
                            <CardTitle>Help us improve the neural network</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium mb-2">Was this pathway helpful?</label>
                              <div className="flex gap-4">
                                <Button
                                  variant={feedback.wasHelpful ? "default" : "outline"}
                                  onClick={() => setFeedback(prev => ({ ...prev, wasHelpful: true }))}
                                  className={feedback.wasHelpful ? "bg-[#daed6e] text-black" : ""}
                                >
                                  Yes
                                </Button>
                                <Button
                                  variant={!feedback.wasHelpful ? "default" : "outline"}
                                  onClick={() => setFeedback(prev => ({ ...prev, wasHelpful: false }))}
                                  className={!feedback.wasHelpful ? "bg-red-500" : ""}
                                >
                                  No
                                </Button>
                              </div>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium mb-2">Rating (1-5):</label>
                              <input
                                type="range"
                                min="1"
                                max="5"
                                value={feedback.rating}
                                onChange={(e) => setFeedback(prev => ({ ...prev, rating: parseInt(e.target.value) }))}
                                className="w-full"
                              />
                              <div className="text-center mt-1">{feedback.rating}/5</div>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium mb-2">Additional feedback:</label>
                              <textarea
                                value={feedback.feedback}
                                onChange={(e) => setFeedback(prev => ({ ...prev, feedback: e.target.value }))}
                                className="w-full p-2 border rounded-lg"
                                rows={3}
                                placeholder="What could be improved?"
                              />
                            </div>
                            
                            <div className="flex gap-2">
                              <Button onClick={handleFeedbackSubmit} className="bg-[#daed6e] hover:bg-[#c5d85c] text-black">
                                Submit Feedback
                              </Button>
                              <Button variant="outline" onClick={() => setShowFeedback(false)}>
                                Cancel
                              </Button>
                          </div>
                          </CardContent>
                        </Card>
                      )}
                        </TabsContent>
                    
                        <TabsContent value="flowchart">
                          <div className="bg-white rounded-lg shadow p-6 border border-[#daed6e]">
                            <MethodologyVisualization methodology={{
                          id: 'neural-pathway',
                          name: 'Neural Pathway',
                              description: 'AI-generated pathway',
                              status: 'Active',
                          steps: neuralPathway.generatedPathway.steps.map((step, idx) => ({
                                id: `step-${idx + 1}`,
                            title: step.title,
                            description: step.description,
                            toolSuggestions: step.tools,
                                toolSuggestionUrls: {},
                            commandExamples: step.commands,
                            guidanceNotes: step.reasoning,
                                requiresUserInput: false,
                            nextStepType: idx < neuralPathway.generatedPathway.steps.length - 1 ? 'Linear' : 'Linear',
                            nextStepId: idx < neuralPathway.generatedPathway.steps.length - 1 ? `step-${idx + 2}` : undefined,
                                conditions: []
                              })),
                              keywords: [],
                              createdAt: '',
                              updatedAt: ''
                            }} />
                          </div>
                        </TabsContent>
                      </Tabs>
                    </div>
              ) : (
                <div className="w-full max-w-2xl mx-auto bg-[#f8fbe7] rounded-xl p-8 shadow text-left">
                  <h2 className="font-bold text-xl mb-4 text-black">Neural Pathway Generator</h2>
                  <p className="mb-6 text-black opacity-80">
                    Our AI analyzes cybersecurity writeups to generate intelligent penetration testing pathways. 
                    The more writeups we have, the smarter the pathways become.
                  </p>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-black font-medium mb-2">
                        Current Stage
                        <input 
                          value={userStage} 
                          onChange={e => setUserStage(e.target.value)} 
                          className="w-full p-3 mt-1 rounded-lg border border-gray-300 focus:border-[#daed6e] focus:ring-1 focus:ring-[#daed6e]" 
                          placeholder="e.g. Reconnaissance, Initial Access, Exploitation" 
                        />
                      </label>
                    </div>
                    
                    <div>
                      <label className="block text-black font-medium mb-2">
                        Target Description
                        <input 
                          value={userTarget} 
                          onChange={e => setUserTarget(e.target.value)} 
                          className="w-full p-3 mt-1 rounded-lg border border-gray-300 focus:border-[#daed6e] focus:ring-1 focus:ring-[#daed6e]" 
                          placeholder="e.g. Banking login portal, admin dashboard, API endpoint" 
                        />
                      </label>
                    </div>
                    
                    <div>
                      <label className="block text-black font-medium mb-2">
                        Key Findings
                        <textarea 
                          value={userFindings} 
                          onChange={e => setUserFindings(e.target.value)} 
                          className="w-full p-3 mt-1 rounded-lg border border-gray-300 focus:border-[#daed6e] focus:ring-1 focus:ring-[#daed6e] min-h-[80px]" 
                          placeholder="e.g. Found subdomains: dev.bank.com, test.bank.com. Default admin email present." 
                        />
                      </label>
                      </div>
                    
                    <div>
                      <label className="block text-black font-medium mb-2">
                        Extra Context
                        <textarea 
                          value={extraContext} 
                          onChange={e => setExtraContext(e.target.value)} 
                          className="w-full p-3 mt-1 rounded-lg border border-gray-300 focus:border-[#daed6e] focus:ring-1 focus:ring-[#daed6e] min-h-[60px]" 
                          placeholder="e.g. User is admin@bank.com, login page uses SSO, found robots.txt, etc." 
                        />
                      </label>
                    </div>
                    
                    <Button 
                      type="submit" 
                      disabled={loading} 
                      className="w-full py-3 font-semibold bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors text-black"
                    >
                      {loading ? "Generating Neural Pathway..." : "Generate Neural Pathway"}
                    </Button>
                  </form>
                    </div>
                  )}
              
              {/* Recent Pathways */}
              {recentPathways.length > 0 && (
                <div className="w-full max-w-4xl mx-auto mt-12">
                  <h3 className="font-bold text-lg mb-4 text-black">Recent Neural Pathways</h3>
                  <div className="grid gap-4">
                    {recentPathways.map((pathway) => (
                      <Card key={pathway.id} className="cursor-pointer hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-medium text-black">{pathway.query}</h4>
                              <p className="text-sm text-gray-600 mt-1">
                                {pathway.generatedPathway.steps.length} steps • 
                                {(pathway.generatedPathway.overallConfidence * 100).toFixed(0)}% confidence
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {(() => {
                                  const date = pathway.createdAt;
                                  if (date && typeof date === 'object' && 'toDate' in date) {
                                    return new Date((date as any).toDate()).toLocaleDateString();
                                  }
                                  return new Date(date).toLocaleDateString();
                                })()}
                              </p>
                            </div>
                            <Badge className={getConfidenceColor(pathway.generatedPathway.overallConfidence)}>
                              {(pathway.generatedPathway.overallConfidence * 100).toFixed(0)}%
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 